import React from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { caseStudiesData } from '../mockData';

export const CaseStudies = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            AI Transformation Success Stories
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Real businesses achieving measurable ROI through AI implementation
          </p>
        </div>
      </section>

      {/* Case Studies List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {caseStudiesData.map((caseStudy, index) => (
              <Card key={caseStudy.id} className="border-0 shadow-xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-2">
                    {/* Image */}
                    <div className={`relative h-96 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img 
                        src={caseStudy.image}
                        alt={caseStudy.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent" />
                      <div className="absolute bottom-6 left-6">
                        <Badge className="bg-white text-blue-900 hover:bg-white text-sm px-4 py-2">
                          {caseStudy.industry}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`p-10 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {caseStudy.title}
                      </h2>
                      <p className="text-blue-900 font-semibold mb-6">
                        Client: {caseStudy.client}
                      </p>

                      {/* Challenge */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full" />
                          Challenge
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {caseStudy.challenge}
                        </p>
                      </div>

                      {/* Solution */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-900 rounded-full" />
                          Solution
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {caseStudy.solution}
                        </p>
                      </div>

                      {/* Results */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-600 rounded-full" />
                          Results
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          {caseStudy.results.map((result, idx) => (
                            <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-green-900 font-semibold text-sm">
                                {result}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Technologies */}
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-600 mb-3">
                          Technologies Used
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {caseStudy.technologies.map((tech, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button 
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                        onClick={() => window.location.href = '/contact'}
                      >
                        Start Your Success Story
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Impact Across Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Measurable results that matter
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { metric: '$50M+', label: 'Cost Savings Delivered' },
              { metric: '95%', label: 'Client Retention Rate' },
              { metric: '10x', label: 'Average ROI' },
              { metric: '150+', label: 'Successful Projects' }
            ].map((stat, index) => (
              <Card key={index} className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <p className="text-5xl font-bold text-blue-900 mb-2">
                    {stat.metric}
                  </p>
                  <p className="text-gray-600">
                    {stat.label}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Highlight */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl bg-gradient-to-br from-blue-900 to-blue-800">
            <CardContent className="p-12 text-center">
              <svg className="w-16 h-16 text-blue-200 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
              </svg>
              <p className="text-2xl text-white mb-8 leading-relaxed">
                "Working with Kpro Data Labs transformed our entire approach to data. Their expertise and dedication resulted in solutions that exceeded our expectations."
              </p>
              <div className="border-t border-blue-200 pt-6">
                <p className="text-white font-semibold text-lg">Jennifer Martinez</p>
                <p className="text-blue-200">CTO, Enterprise Tech Corp</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Let's discuss how we can help you achieve similar results
          </p>
          <Button 
            size="lg"
            className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-10 py-6 h-auto font-semibold"
            onClick={() => window.location.href = '/contact'}
          >
            Get Started Today
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};
